/**
 * SatelliteAtom - A status-colored satellite dot
 *
 * Renders a small circular indicator representing a related sphere (satellite).
 * Color is determined by the satellite's status.
 */

import React from 'react';
import type { SatelliteSphere } from 'types/global';
import { getStatusColor } from '../../../utils/statusColors';
import type { ExtendedStatusValue } from '../../../utils/statusColors';
import './SatelliteAtom.scss';

export interface SatelliteAtomProps {
  /** Satellite sphere data */
  satellite: SatelliteSphere;
  /** Size of the satellite dot in pixels (default: 4) */
  size?: number;
  /** Position on the orbit (angle in radians) */
  angle?: number;
  /** Distance from center (orbit radius) */
  orbitRadius?: number;
  /** Additional CSS class */
  className?: string;
}

const SatelliteAtom: React.FC<SatelliteAtomProps> = ({
  satellite,
  size = 4,
  angle = 0,
  orbitRadius = 35,
  className = '',
}) => {
  // Get status color
  const statusColor = getStatusColor(satellite.status as ExtendedStatusValue);

  // Calculate position on orbit
  const cx = orbitRadius * Math.cos(angle);
  const cy = orbitRadius * Math.sin(angle);

  return (
    <circle
      className={`satellite-atom ${className}`}
      cx={cx}
      cy={cy}
      r={size}
      fill={statusColor}
      stroke={statusColor}
      strokeWidth={1}
    >
      <title>{satellite.name}</title>
    </circle>
  );
};

export default SatelliteAtom;
